import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import StarIcon from "@material-ui/icons/Star";
import RateReviewIcon from "@material-ui/icons/RateReview";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ChatIcon from "@material-ui/icons/Chat";
import TicketHistoryModal from "../TicketHistoryModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const NpsReportModal = ({ open, onClose, defaultWhatsappId }) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [whatsapps, setWhatsapps] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(defaultWhatsappId || "all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [periodDays, setPeriodDays] = useState(30);
  const [selectedHistoryTicket, setSelectedHistoryTicket] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const handleOpenHistory = (rec) => {
    setSelectedHistoryTicket({
      id: rec.ticketId,
      contact: { name: rec.contactName },
      createdAt: rec.ratingAt || new Date().toISOString(),
      status: "closed"
    });
    setHistoryModalOpen(true);
  };

  useEffect(() => {
    if (defaultWhatsappId) {
      setSelectedWhatsapp(defaultWhatsappId);
    }
  }, [defaultWhatsappId]);

  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        const [wRes, uRes] = await Promise.all([
          api.get("/whatsapp"),
          api.get("/users")
        ]);
        setWhatsapps(wRes.data || []);
        setUsers(uRes.data?.users || uRes.data || []);
      } catch (err) {
        //
      }
    };
    if (open) {
      fetchFiltersData();
    }
  }, [open]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = {
        whatsappId: selectedWhatsapp !== "all" ? selectedWhatsapp : undefined,
        userId: selectedUser !== "all" ? selectedUser : undefined,
        days: periodDays > 0 ? periodDays : undefined
      };
      const { data } = await api.get("/whatsapp/nps-report", { params });
      setReportData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadReport();
    }
  }, [open, selectedWhatsapp, selectedUser, periodDays]);

  const overall = reportData?.overall || { totalEvaluations: 0, avgRating: 0, breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };

  const getScoreColor = (score) => {
    if (score >= 4.5) return "#16a34a"; // green
    if (score >= 3.8) return "#0284c7"; // blue
    if (score >= 3.0) return "#d97706"; // amber
    return "#dc2626"; // red
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        disableTypography
        style={{
          backgroundColor: "#0f172a",
          color: "#fff",
          padding: "16px 24px"
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <RateReviewIcon style={{ color: "#f59e0b", fontSize: 28 }} />
            <div>
              <Typography variant="h6" style={{ fontWeight: 800, color: "#fff", fontSize: "1.1rem", lineHeight: 1.2 }}>
                ⭐ Pesquisa de Satisfação & NPS dos Atendimentos
              </Typography>
              <Typography variant="caption" style={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                Média de avaliações dos clientes recebidas após o encerramento dos tickets
              </Typography>
            </div>
          </div>
          <IconButton onClick={onClose} style={{ color: "#cbd5e1" }} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent style={{ backgroundColor: "#f8fafc", padding: "20px 24px" }}>
        {/* FILTROS SUPERIORES */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 20,
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            padding: "12px 16px",
            borderRadius: 10,
            border: "1px solid #e2e8f0"
          }}
        >
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel>Conexão / Marca</InputLabel>
              <Select
                value={selectedWhatsapp}
                onChange={(e) => setSelectedWhatsapp(e.target.value)}
                label="Conexão / Marca"
              >
                <MenuItem value="all">Todas as Conexões</MenuItem>
                {whatsapps.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: w.color || "#2576D2",
                        marginRight: 8
                      }}
                    />
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel>Atendente / Responsável</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label="Atendente / Responsável"
              >
                <MenuItem value="all">Todos os Atendentes</MenuItem>
                <MenuItem value="ai">🤖 Assistente Virtual (IA)</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    👤 {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={periodDays === 7 ? "contained" : "outlined"}
              color={periodDays === 7 ? "primary" : "default"}
              onClick={() => setPeriodDays(7)}
            >
              7 Dias
            </Button>
            <Button
              variant={periodDays === 30 ? "contained" : "outlined"}
              color={periodDays === 30 ? "primary" : "default"}
              onClick={() => setPeriodDays(30)}
            >
              30 Dias
            </Button>
            <Button
              variant={periodDays === 90 ? "contained" : "outlined"}
              color={periodDays === 90 ? "primary" : "default"}
              onClick={() => setPeriodDays(90)}
            >
              90 Dias
            </Button>
            <Button
              variant={periodDays === 0 ? "contained" : "outlined"}
              color={periodDays === 0 ? "primary" : "default"}
              onClick={() => setPeriodDays(0)}
            >
              Tudo
            </Button>
          </ButtonGroup>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* CARDS DE RESUMO */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              {/* CARD MÉDIA GERAL */}
              <Paper
                elevation={1}
                style={{
                  padding: 20,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                  color: "#fff"
                }}
              >
                <div>
                  <Typography style={{ fontSize: 13, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>
                    ⭐ Média de Satisfação
                  </Typography>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
                    <Typography style={{ fontSize: 38, fontWeight: 900, color: getScoreColor(overall.avgRating) }}>
                      {overall.totalEvaluations > 0 ? overall.avgRating.toFixed(2) : "—"}
                    </Typography>
                    <Typography style={{ fontSize: 18, color: "#64748b", fontWeight: 700 }}>
                      / 5.0
                    </Typography>
                  </div>
                  <Typography style={{ fontSize: 12, color: "#cbd5e1", marginTop: 4 }}>
                    {overall.totalEvaluations} {overall.totalEvaluations === 1 ? "avaliação recebida" : "avaliações recebidas"}
                  </Typography>
                </div>
                <div style={{ fontSize: 48 }}>
                  {overall.avgRating >= 4.5 ? "😍" : overall.avgRating >= 3.8 ? "😊" : overall.avgRating >= 3.0 ? "😐" : "⭐"}
                </div>
              </Paper>

              {/* CARD DISTRIBUIÇÃO DAS NOTAS */}
              <Paper
                elevation={1}
                style={{
                  padding: 16,
                  borderRadius: 12,
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0"
                }}
              >
                <Typography style={{ fontSize: 13, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase" }}>
                  📊 Distribuição das Notas (1 a 5)
                </Typography>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = overall.breakdown[star] || 0;
                    const pct = overall.totalEvaluations > 0 ? Math.round((count / overall.totalEvaluations) * 100) : 0;
                    return (
                      <div key={star} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem" }}>
                        <span style={{ width: 45, fontWeight: 700, color: "#334155" }}>
                          {star} ⭐
                        </span>
                        <div style={{ flex: 1, backgroundColor: "#f1f5f9", height: 8, borderRadius: 4, overflow: "hidden" }}>
                          <div
                            style={{
                              width: `${pct}%`,
                              height: "100%",
                              backgroundColor: star >= 4 ? "#16a34a" : star === 3 ? "#d97706" : "#dc2626"
                            }}
                          />
                        </div>
                        <span style={{ width: 60, textAlign: "right", color: "#64748b", fontSize: "0.75rem" }}>
                          {count} ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Paper>
            </div>

            {/* TABELA 1: MÉDIA POR ATENDENTE (HUMANO + IA) */}
            <Paper elevation={1} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
              <div style={{ padding: "14px 18px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography style={{ fontWeight: 800, fontSize: 14, color: "#1e293b" }}>
                  👩‍💼 Desempenho e Média de Satisfação por Atendente
                </Typography>
              </div>
              <Table size="small">
                <TableHead style={{ backgroundColor: "#f1f5f9" }}>
                  <TableRow>
                    <TableCell style={{ fontWeight: 700 }}>Atendente</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Avaliações</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Nota Média</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>5 ⭐</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>4 ⭐</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>3 ⭐</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>1-2 ⭐</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!reportData?.byUser || reportData.byUser.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" style={{ padding: 24, color: "#94a3b8" }}>
                        Nenhuma avaliação registrada para os filtros selecionados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.byUser.map((u) => (
                      <TableRow key={String(u.userId)} hover>
                        <TableCell style={{ fontWeight: 700, color: "#1e293b" }}>
                          {u.userId === null ? "🤖 Assistente Virtual (IA)" : `👤 ${u.userName}`}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={`${u.totalEvaluations} avaliações`} size="small" style={{ fontSize: "0.75rem", backgroundColor: "#e2e8f0" }} />
                        </TableCell>
                        <TableCell align="center">
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontWeight: 800,
                              fontSize: "0.95rem",
                              color: getScoreColor(u.avgRating)
                            }}
                          >
                            <StarIcon style={{ fontSize: 18, color: "#f59e0b" }} />
                            {u.avgRating.toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell align="center" style={{ color: "#16a34a", fontWeight: 700 }}>{u.breakdown[5] || 0}</TableCell>
                        <TableCell align="center" style={{ color: "#0284c7", fontWeight: 600 }}>{u.breakdown[4] || 0}</TableCell>
                        <TableCell align="center" style={{ color: "#d97706" }}>{u.breakdown[3] || 0}</TableCell>
                        <TableCell align="center" style={{ color: "#dc2626" }}>{(u.breakdown[2] || 0) + (u.breakdown[1] || 0)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>

            {/* TABELA 2: MÉDIA POR CONEXÃO */}
            <Paper elevation={1} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
              <div style={{ padding: "14px 18px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Typography style={{ fontWeight: 800, fontSize: 14, color: "#1e293b" }}>
                  🌐 Média de Satisfação por Conexão / Marca
                </Typography>
              </div>
              <Table size="small">
                <TableHead style={{ backgroundColor: "#f1f5f9" }}>
                  <TableRow>
                    <TableCell style={{ fontWeight: 700 }}>Conexão</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Total de Avaliações</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>Nota Média</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700 }}>% Aprovação (4-5 ⭐)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!reportData?.byConnection || reportData.byConnection.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" style={{ padding: 24, color: "#94a3b8" }}>
                        Nenhuma avaliação registrada por conexão.
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.byConnection.map((conn) => {
                      const posCount = (conn.breakdown[5] || 0) + (conn.breakdown[4] || 0);
                      const posPct = conn.totalEvaluations > 0 ? Math.round((posCount / conn.totalEvaluations) * 100) : 0;
                      return (
                        <TableRow key={conn.whatsappId} hover>
                          <TableCell style={{ fontWeight: 700 }}>
                            <span
                              style={{
                                display: "inline-block",
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: conn.whatsappColor || "#2576D2",
                                marginRight: 8
                              }}
                            />
                            {conn.whatsappName}
                          </TableCell>
                          <TableCell align="center">{conn.totalEvaluations}</TableCell>
                          <TableCell align="center">
                            <span style={{ fontWeight: 800, color: getScoreColor(conn.avgRating) }}>
                              ⭐ {conn.avgRating.toFixed(2)}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${posPct}% Positivo`}
                              size="small"
                              style={{
                                backgroundColor: posPct >= 80 ? "#dcfce7" : "#fef3c7",
                                color: posPct >= 80 ? "#166534" : "#92400e",
                                fontWeight: 700
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Paper>

            {/* TABELA 3: ATENDIMENTOS AVALIADOS RECENTEMENTE */}
            {reportData?.recentEvaluations && reportData.recentEvaluations.length > 0 && (
              <Paper elevation={1} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}>
                <div style={{ padding: "14px 18px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <Typography style={{ fontWeight: 800, fontSize: 14, color: "#1e293b" }}>
                    ⭐ Avaliações Recentes Recebidas
                  </Typography>
                </div>
                <Table size="small">
                  <TableHead style={{ backgroundColor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell style={{ fontWeight: 700 }}>Ticket</TableCell>
                      <TableCell style={{ fontWeight: 700 }}>Cliente</TableCell>
                      <TableCell style={{ fontWeight: 700 }}>Atendente</TableCell>
                      <TableCell style={{ fontWeight: 700 }}>Conexão</TableCell>
                      <TableCell align="center" style={{ fontWeight: 700 }}>Nota</TableCell>
                      <TableCell align="right" style={{ fontWeight: 700 }}>Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.recentEvaluations.slice(0, 15).map((rec) => (
                      <TableRow key={rec.ticketId} hover>
                        <TableCell style={{ fontWeight: 700 }}>
                          <span
                            onClick={() => handleOpenHistory(rec)}
                            style={{
                              color: "#2563eb",
                              fontWeight: 700,
                              cursor: "pointer",
                              textDecoration: "underline"
                            }}
                            title="Clique para ler conversa rápida"
                          >
                            #{rec.ticketId}
                          </span>
                        </TableCell>
                        <TableCell>{rec.contactName}</TableCell>
                        <TableCell>{rec.userName}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: rec.whatsappColor || "#2576D2",
                              marginRight: 6
                            }}
                          />
                          {rec.whatsappName}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${rec.rating} ⭐`}
                            size="small"
                            style={{
                              backgroundColor: rec.rating >= 4 ? "#dcfce7" : rec.rating === 3 ? "#fef3c7" : "#fee2e2",
                              color: rec.rating >= 4 ? "#166534" : rec.rating === 3 ? "#92400e" : "#991b1b",
                              fontWeight: 800
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<ChatIcon style={{ fontSize: "0.85rem" }} />}
                              onClick={() => handleOpenHistory(rec)}
                              style={{ textTransform: "none", fontSize: "0.75rem", padding: "2px 8px" }}
                            >
                              Ler Conversa
                            </Button>
                            <Tooltip title="Abrir em nova aba">
                              <IconButton
                                size="small"
                                component="a"
                                href={`/tickets/${rec.ticketId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ padding: 4, color: "#64748b" }}
                              >
                                <OpenInNewIcon style={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </div>
        )}

        {/* MODAL DE HISTÓRICO RÁPIDO DO TICKET (SEM SAIR DA TELA DE NPS) */}
        {selectedHistoryTicket && (
          <TicketHistoryModal
            open={historyModalOpen}
            onClose={() => {
              setHistoryModalOpen(false);
              setSelectedHistoryTicket(null);
            }}
            ticket={selectedHistoryTicket}
          />
        )}
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NpsReportModal;
